import { createSelector, createSlice } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

const today = dayjs()
const initialState = {
  currentStepIndex: 0,
  currentBooking: {
    date: {
      selectedDate: undefined,
      currentCalendarYear: today.get('year'),
      currentCalendarMonth: today.get('month'),
      selectedTime: undefined,
    },
    calendarId: undefined,
    slotId: undefined,
  },
  bookedAppointments: [],
}

export const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    setStepIndex: (state, action) => {
      state.currentStepIndex = action.payload
    },
    setSelectedDate: (state, action) => {
      state.currentBooking.date.selectedDate = action.payload
      state.currentBooking.date.selectedTime = undefined
    },
    setSelectedTime: (state, action) => {
      state.currentBooking.date.selectedTime = action.payload
    },
    setCurrentCalendarYear: (state, action) => {
      state.currentBooking.date.currentCalendarYear = action.payload
    },
    setCurrentCalendarMonth: (state, action) => {
      state.currentBooking.date.currentCalendarMonth = action.payload
    },
    updateCurrentBooking: (state, action) => {
      state.currentBooking[action.payload.prop] = action.payload.value
    },
    setCurrentBooking: (state, action) => {
      state.currentBooking = action.payload
    },
    setBookedAppointments: (state, action) => {
      state.bookedAppointments = action.payload
    },
    clearCurrentBooking: (state) => {
      state.currentBooking = {
        date: {
          selectedDate: undefined,
          setSelectedTime: undefined,
          currentCalendarYear: today.get('year'),
          currentCalendarMonth: today.get('month'),
        },
      }
      state.currentStepIndex = 0
    },
  },
})

export const {
  setStepIndex,
  setSelectedDate,
  setSelectedTime,
  setCurrentCalendarMonth,
  setCurrentCalendarYear,
  updateCurrentBooking,
  setCurrentBooking,
  setBookedAppointments,
  clearCurrentBooking,
} = bookingSlice.actions

// Selectors
export const canUserMakeAppointment = createSelector(
  [
    (state) => state.app.appSettings.nextDonationWait,
    (state) => state.app.appSettings.maxAllowedComingAppointments,
    (state) => state.booking.bookedAppointments,
    (state) => state.app.user.donorDonationTypeDatas,
    (state) => state.app.user.lastDonationDate,
  ],
  (
    nextDonationWait,
    maxAllowedComingAppointments,
    bookedAppointments,
    donorDonationTypeDatas,
    lastDonationDate,
  ) => {
    const userDonationTypes = _.map(donorDonationTypeDatas, (d) => d.donationTypeEnum)

    const donationTypeAllowed = _.map(userDonationTypes, (donationType) => {
      const filteredAppointments = _.filter(bookedAppointments, { donationTypeEnum: donationType })
      const bookedDonationDate = _.get(
        _(filteredAppointments).sortBy(['fromDate']).first(),
        'fromDate',
      )

      const furthestBookedDonationDate = _.get(
        _(filteredAppointments).sortBy(['fromDate']).last(),
        'fromDate',
      )

      const waitingTime = _.get(
        _.find(nextDonationWait, { donationTypeEnum: donationType }),
        'daysToWait',
      )

      const hasMaxNumAppointments =
        _.size(filteredAppointments) >= maxAllowedComingAppointments[donationType]

      const needToWait =
        waitingTime === undefined ||
        dayjs().diff(dayjs(bookedDonationDate || lastDonationDate), 'days') <= waitingTime

      const daysToBookable = Math.max(
        0,
        dayjs(furthestBookedDonationDate || lastDonationDate).diff(dayjs(), 'days') + waitingTime,
      )

      // console.table({
      //   hasMaxNumAppointments,
      //   needToWait,
      //   diff: dayjs().diff(dayjs(bookedDonationDate || lastDonationDate), 'days'),
      //   waitingTime,
      //   lastDonationDate
      // })

      return {
        daysToBookable,
        donationType,
        canBook: !hasMaxNumAppointments || (hasMaxNumAppointments && !needToWait),
        reason: _.compact([
          hasMaxNumAppointments && 'hasMaxNumAppointments',
          needToWait && 'needToWait',
        ]),
      }
    })

    return {
      allowed: _.some(donationTypeAllowed, { canBook: true }),
      ...donationTypeAllowed,
    }
  },
)

export default bookingSlice.reducer
